import React, { useState, useEffect, useCallback } from 'react';
import api from '../api/axios';
import { useAuth } from '../context/AuthContext';
import ImageUploader from '../components/ImageUploader';

// ─────────────────────────────────────────────────────────────────────────────
const CATEGORIES = ['Centre Shop', 'Ceremony Items', 'SevaOffering', 'Deity Attire', 'Incense & Lamps', 'Sacred Herbs', 'Devotional Books', 'Other'];

const fmt = (n) => (n || 0).toLocaleString('en-IN');

const getStockStyle = (stock) => {
  if (stock <= 0)  return { color: '#f87171', bg: 'rgba(248,113,113,0.1)', label: 'Out of Stock' };
  if (stock < 10)  return { color: '#f59e0b', bg: 'rgba(245,158,11,0.1)',  label: `Low (${stock})` };
  return               { color: '#22c55e', bg: 'rgba(34,197,94,0.1)',   label: `In Stock (${stock})` };
};

// ─────────────────────────────────────────────────────────────────────────────
// Empty State
// ─────────────────────────────────────────────────────────────────────────────
const EmptyState = ({ onAdd }) => (
  <div style={{ textAlign: 'center', padding: '7rem 0' }}>
    <div style={{ fontSize: 72, opacity: 0.1, marginBottom: 16 }}>🛕</div>
    <p style={{ color: 'rgba(212,175,55,0.5)', fontSize: 18, fontWeight: 700, marginBottom: 6 }}>
      No Centre Shop Products Yet
    </p>
    <p style={{ color: 'rgba(255,255,255,0.2)', fontSize: 13, marginBottom: 24 }}>
      Add products that will appear in centre live streams.
    </p>
    <button onClick={onAdd} style={{
      background: 'rgba(212,175,55,0.12)', border: '1px solid rgba(212,175,55,0.3)',
      color: '#E8B84B', padding: '10px 24px', borderRadius: 12,
      fontSize: 12, fontWeight: 900, letterSpacing: '0.15em', textTransform: 'uppercase', cursor: 'pointer'
    }}>
      + Add First Product
    </button>
  </div>
);

// ─────────────────────────────────────────────────────────────────────────────
// Product Modal (Admin only — multi-centre, no vendors)
// ─────────────────────────────────────────────────────────────────────────────
const ProductModal = ({ mode, initial, centres, onSave, onClose, saving }) => {
  const [form, setForm] = useState({
    name:            initial?.name     || '',
    price:           initial?.price    || '',
    stock:           initial?.stock    || '',
    category:        initial?.category || 'Centre Shop',
    image:           initial?.image    || '',
    assignedCentres: (initial?.assignedCentres || []).map(t => t._id || t),
  });
  const [centreSearch, setCentreSearch] = useState('');

  const set = (k, v) => setForm(f => ({ ...f, [k]: v }));

  const toggleCentre = (id) => {
    setForm(f => ({
      ...f,
      assignedCentres: f.assignedCentres.includes(id)
        ? f.assignedCentres.filter(t => t !== id)
        : [...f.assignedCentres, id]
    }));
  };

  const filteredCentres = centres.filter(t =>
    t.name?.toLowerCase().includes(centreSearch.toLowerCase())
  );

  const inputStyle = {
    width: '100%', padding: '12px 16px', borderRadius: 12, fontSize: 13,
    background: 'rgba(255,255,255,0.05)', border: '1px solid rgba(212,175,55,0.2)',
    color: '#FDF6E3', outline: 'none', boxSizing: 'border-box'
  };
  const labelStyle = {
    display: 'block', fontSize: 9, fontWeight: 900,
    textTransform: 'uppercase', letterSpacing: '0.2em',
    color: 'rgba(212,175,55,0.55)', marginBottom: 6
  };

  return (
    <div
      onClick={e => { if (e.target === e.currentTarget) onClose(); }}
      style={{
        position: 'fixed', inset: 0, zIndex: 50, display: 'flex',
        alignItems: 'center', justifyContent: 'center', padding: 16,
        background: 'rgba(0,0,0,0.78)', backdropFilter: 'blur(10px)'
      }}
    >
      <div style={{
        width: '100%', maxWidth: 520, borderRadius: 24, padding: 32,
        background: 'linear-gradient(160deg, #1a0808 0%, #120505 100%)',
        border: '1px solid rgba(212,175,55,0.22)',
        boxShadow: '0 40px 100px rgba(0,0,0,0.7)',
        maxHeight: '90vh', overflowY: 'auto'
      }}>
        {/* Header */}
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 28 }}>
          <div>
            <p style={{ fontSize: 9, fontWeight: 900, textTransform: 'uppercase', letterSpacing: '0.3em', color: 'rgba(212,175,55,0.4)', marginBottom: 4 }}>
              Admin · Centre Shop
            </p>
            <h2 style={{ fontFamily: "'Playfair Display', serif", fontSize: 20, color: '#FDF6E3', fontWeight: 700 }}>
              {mode === 'edit' ? '✏️ Edit Product' : '➕ Add Product'}
            </h2>
          </div>
          <button onClick={onClose} style={{
            width: 36, height: 36, borderRadius: 10, border: 'none',
            background: 'rgba(255,255,255,0.07)', color: 'rgba(255,255,255,0.5)',
            cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 18
          }}>×</button>
        </div>

        <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
          {/* Name */}
          <div>
            <label style={labelStyle}>Product Name *</label>
            <input style={inputStyle} type="text" value={form.name}
              onChange={e => set('name', e.target.value)} placeholder="e.g. Sacred Sandalwood Paste" />
          </div>

          {/* Price + Stock */}
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
            <div>
              <label style={labelStyle}>Price (₹) *</label>
              <input style={inputStyle} type="number" min="0" value={form.price}
                onChange={e => set('price', e.target.value)} placeholder="0" />
            </div>
            <div>
              <label style={labelStyle}>Stock Qty *</label>
              <input style={inputStyle} type="number" min="0" value={form.stock}
                onChange={e => set('stock', e.target.value)} placeholder="0" />
            </div>
          </div>

          {/* Category */}
          <div>
            <label style={labelStyle}>Category</label>
            <select style={inputStyle} value={form.category} onChange={e => set('category', e.target.value)}>
              {CATEGORIES.map(c => <option key={c} value={c} style={{ background: '#1a0808' }}>{c}</option>)}
            </select>
          </div>

          {/* ── Product Image ── */}
          <div>
            <label style={labelStyle}>Product Image</label>
            <div style={{
              display: 'flex', alignItems: 'center', gap: 16,
              padding: 16, borderRadius: 16,
              background: 'rgba(255,255,255,0.03)', border: '1px solid rgba(212,175,55,0.15)'
            }}>
              {/* Preview */}
              {form.image ? (
                <div style={{ position: 'relative', flexShrink: 0 }}>
                  <img
                    src={form.image} alt="preview"
                    style={{ width: 72, height: 72, borderRadius: 12, objectFit: 'cover', border: '1px solid rgba(212,175,55,0.25)' }}
                    onError={e => { e.target.style.display = 'none'; }}
                  />
                </div>
              ) : (
                <div style={{
                  width: 72, height: 72, borderRadius: 12, flexShrink: 0,
                  background: 'rgba(212,175,55,0.06)', border: '2px dashed rgba(212,175,55,0.2)',
                  display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center'
                }}>
                  <span style={{ fontSize: 24 }}>🪔</span>
                  <span style={{ fontSize: 8, color: 'rgba(255,255,255,0.2)', marginTop: 2 }}>No image</span>
                </div>
              )}

              {/* Uploader */}
              <div style={{ flex: 1 }}>
                <ImageUploader
                  buttonText={form.image ? 'Change Image' : 'Upload Image'}
                  folder="centre-shop"
                  oldUrl={form.image || null}
                  onUploadSuccess={(url) => set('image', url)}
                  onError={(msg) => alert('Upload failed: ' + msg)}
                />
                <p style={{ fontSize: 10, color: 'rgba(255,255,255,0.2)', marginTop: 6 }}>
                  JPG, PNG or WebP · stored in Cloudinary
                </p>
              </div>
            </div>
          </div>

          {/* ── Assign Centres (multi-select) ── */}
          <div>
            <label style={labelStyle}>
              Assign to Centres *
              <span style={{ fontWeight: 400, textTransform: 'none', letterSpacing: 0, color: 'rgba(255,255,255,0.2)', marginLeft: 8 }}>
                ({form.assignedCentres.length} selected — product shows in their live streams)
              </span>
            </label>

            {/* Centre search */}
            <input
              type="text" value={centreSearch}
              onChange={e => setCentreSearch(e.target.value)}
              placeholder="Search centres..."
              style={{
                ...inputStyle, fontSize: 12, padding: '8px 12px',
                marginBottom: 8, border: '1px solid rgba(212,175,55,0.15)'
              }}
            />

            {/* Checkbox list */}
            <div style={{
              maxHeight: 180, overflowY: 'auto', borderRadius: 12,
              border: '1px solid rgba(212,175,55,0.15)', background: 'rgba(0,0,0,0.2)'
            }}>
              {filteredCentres.length === 0 ? (
                <p style={{ textAlign: 'center', padding: '16px 0', fontSize: 11, color: 'rgba(255,255,255,0.2)' }}>
                  No centres found
                </p>
              ) : filteredCentres.map(t => {
                const checked = form.assignedCentres.includes(t._id);
                return (
                  <label key={t._id}
                    style={{
                      display: 'flex', alignItems: 'center', gap: 12,
                      padding: '10px 16px', cursor: 'pointer',
                      borderBottom: '1px solid rgba(255,255,255,0.04)',
                      background: checked ? 'rgba(212,175,55,0.06)' : 'transparent',
                      transition: 'background 0.15s'
                    }}>
                    {/* Custom checkbox */}
                    <div style={{
                      width: 16, height: 16, borderRadius: 6, flexShrink: 0,
                      display: 'flex', alignItems: 'center', justifyContent: 'center',
                      background: checked ? 'rgba(212,175,55,0.25)' : 'rgba(255,255,255,0.06)',
                      border: `1px solid ${checked ? 'rgba(212,175,55,0.6)' : 'rgba(255,255,255,0.12)'}`,
                      transition: 'all 0.15s'
                    }}>
                      {checked && <span style={{ color: '#E8B84B', fontSize: 11, fontWeight: 900 }}>✓</span>}
                    </div>
                    <input type="checkbox" checked={checked} onChange={() => toggleCentre(t._id)} style={{ display: 'none' }} />
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <p style={{ fontSize: 13, fontWeight: 700, color: checked ? '#E8B84B' : 'rgba(255,255,255,0.75)', margin: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                        🛕 {t.name}
                      </p>
                      {t.location && (
                        <p style={{ fontSize: 10, color: 'rgba(255,255,255,0.25)', margin: 0 }}>{t.location}</p>
                      )}
                    </div>
                  </label>
                );
              })}
            </div>

            {form.assignedCentres.length > 0 && (
              <button onClick={() => set('assignedCentres', [])}
                style={{ marginTop: 6, fontSize: 9, fontWeight: 700, color: 'rgba(248,113,113,0.55)', background: 'none', border: 'none', cursor: 'pointer', padding: 0 }}>
                ✕ Clear all centres
              </button>
            )}
          </div>
        </div>

        {/* Actions */}
        <div style={{ display: 'flex', gap: 12, marginTop: 28 }}>
          <button onClick={onClose} style={{
            flex: 1, padding: '12px 0', borderRadius: 12, border: 'none',
            background: 'rgba(255,255,255,0.06)', color: 'rgba(255,255,255,0.45)',
            fontSize: 13, fontWeight: 700, cursor: 'pointer'
          }}>Cancel</button>
          <button
            onClick={() => onSave({ ...form, isCentreShop: true, assignedVendors: [] })}
            disabled={saving || !form.name || !form.price || !form.stock || form.assignedCentres.length === 0}
            style={{
              flex: 2, padding: '12px 0', borderRadius: 12,
              background: 'rgba(212,175,55,0.15)', border: '1px solid rgba(212,175,55,0.35)',
              color: '#E8B84B', fontSize: 12, fontWeight: 900, letterSpacing: '0.12em',
              textTransform: 'uppercase', cursor: saving ? 'not-allowed' : 'pointer',
              opacity: (saving || !form.name || !form.price || !form.stock || form.assignedCentres.length === 0) ? 0.4 : 1,
              transition: 'opacity 0.2s'
            }}
          >
            {saving ? 'Saving...' : mode === 'edit' ? 'Save Changes' : 'Add Product'}
          </button>
        </div>
      </div>
    </div>
  );
};

// ─────────────────────────────────────────────────────────────────────────────
// Main Component — Admin only
// ─────────────────────────────────────────────────────────────────────────────
const CentreShop = () => {
  const { user } = useAuth();
  const isAdmin = user?.role === 'ADMIN' || user?.role === 'SUPER_ADMIN';

  const [products, setProducts]   = useState([]);
  const [centres, setCentres]     = useState([]);
  const [loading, setLoading]     = useState(true);
  const [saving, setSaving]       = useState(false);
  const [error, setError]         = useState(null);
  const [success, setSuccess]     = useState(null);

  const [filterCentre, setFilterCentre] = useState('');
  const [filterCat,    setFilterCat]    = useState('');
  const [search,       setSearch]       = useState('');
  const [modal, setModal]               = useState(null); // null | { mode, data? }
  const [deleting, setDeleting]         = useState(null);

  // ── Fetch ──────────────────────────────────────────────────────────────────
  const fetchAll = useCallback(async () => {
    setLoading(true);
    try {
      const [prodRes, centreRes] = await Promise.allSettled([
        api.get('/centre-shop'),
        api.get('/admin/centres'),
      ]);
      if (prodRes.status === 'fulfilled')   setProducts(prodRes.value.data?.data || []);
      if (centreRes.status === 'fulfilled') setCentres(centreRes.value.data?.data || []);
    } catch {
      setError('Failed to load data.');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchAll(); }, [fetchAll]);

  // Auto-dismiss alerts
  useEffect(() => { if (success) { const t = setTimeout(() => setSuccess(null), 3000); return () => clearTimeout(t); } }, [success]);
  useEffect(() => { if (error)   { const t = setTimeout(() => setError(null),   4000); return () => clearTimeout(t); } }, [error]);

  // ── Save ───────────────────────────────────────────────────────────────────
  const handleSave = async (form) => {
    setSaving(true);
    try {
      // Remap assignedCentres → centreIds so the backend createProductController reads them correctly.
      // The backend reads centreIds[] and builds both centreId (first) + assignedCentres (full array).
      const { assignedCentres, ...rest } = form;
      const payload = {
        ...rest,
        centreIds:  assignedCentres || [],          // backend key
        centreId:   (assignedCentres || [])[0] || null, // first centre (backward compat)
        isCentreShop: true,
        vendorId:   null,
      };

      if (modal?.mode === 'edit') {
        await api.put(`/admin/products/${modal.data._id}`, payload);
        setSuccess('Centre Shop product updated!');
      } else {
        await api.post('/admin/products', payload);
        setSuccess('Product added to Centre Shop!');
      }
      setModal(null);
      fetchAll();
    } catch (e) {
      setError(e.response?.data?.message || 'Failed to save product.');
    } finally {
      setSaving(false);
    }
  };

  // ── Delete ─────────────────────────────────────────────────────────────────
  const handleDelete = async (id) => {
    setDeleting(id);
    try {
      await api.delete(`/admin/products/${id}`);
      setSuccess('Product removed.');
      setProducts(prev => prev.filter(p => p._id !== id));
    } catch (e) {
      setError(e.response?.data?.message || 'Failed to delete.');
    } finally {
      setDeleting(null);
    }
  };

  // ── Filter ─────────────────────────────────────────────────────────────────
  const filtered = products.filter(p => {
    const temps = (p.assignedCentres || []).map(t => t._id || t);
    const matchCentre = !filterCentre || temps.includes(filterCentre);
    const matchCat    = !filterCat    || p.category === filterCat;
    const matchSearch = !search       || p.name.toLowerCase().includes(search.toLowerCase());
    return matchCentre && matchCat && matchSearch;
  });

  // ── Stats ──────────────────────────────────────────────────────────────────
  const totalValue  = products.reduce((s, p) => s + (p.price || 0) * (p.stock || 0), 0);
  const lowStock    = products.filter(p => p.stock > 0 && p.stock < 10).length;
  const outOfStock  = products.filter(p => p.stock <= 0).length;
  const centreCount = new Set(products.flatMap(p => (p.assignedCentres || []).map(t => t._id || t))).size;

  // ── Loading ────────────────────────────────────────────────────────────────
  if (loading) {
    return (
      <div style={{ minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center', background: '#0e0404' }}>
        <div style={{ textAlign: 'center' }}>
          <div style={{
            width: 56, height: 56, borderRadius: '50%',
            border: '4px solid rgba(212,175,55,0.15)', borderTopColor: '#E8B84B',
            animation: 'spin 0.8s linear infinite', margin: '0 auto 20px'
          }} />
          <p style={{ color: '#E8B84B', letterSpacing: '0.25em', fontSize: 12, fontWeight: 700 }}>LOADING CENTRE SHOP...</p>
        </div>
        <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>
      </div>
    );
  }

  // ── Render ─────────────────────────────────────────────────────────────────
  return (
    <div style={{ minHeight: '100vh', paddingBottom: 80, background: 'linear-gradient(160deg, #0e0404 0%, #1a0808 50%, #120606 100%)' }}>

      {/* ── Header ── */}
      <div style={{
        position: 'sticky', top: 0, zIndex: 30, padding: '20px 32px',
        background: 'rgba(14,4,4,0.94)', backdropFilter: 'blur(20px)',
        borderBottom: '1px solid rgba(212,175,55,0.1)',
        display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 12
      }}>
        <div>
          <p style={{ fontSize: 9, fontWeight: 900, textTransform: 'uppercase', letterSpacing: '0.35em', color: 'rgba(212,175,55,0.4)', marginBottom: 3 }}>
            Admin Panel · Sacred Commerce
          </p>
          <h1 style={{ fontFamily: "'Playfair Display', serif", fontSize: 22, color: '#FDF6E3', fontWeight: 700, marginBottom: 2 }}>
            🛕 Centre Shop
          </h1>
          <p style={{ fontSize: 12, color: 'rgba(255,255,255,0.22)' }}>
            Admin-managed products · Appears in each assigned centre's live stream
          </p>
        </div>
        {isAdmin && (
          <button onClick={() => setModal({ mode: 'add' })} style={{
            display: 'flex', alignItems: 'center', gap: 8,
            padding: '10px 20px', borderRadius: 12,
            background: 'rgba(212,175,55,0.13)', border: '1px solid rgba(212,175,55,0.32)',
            color: '#E8B84B', fontSize: 11, fontWeight: 900, letterSpacing: '0.15em',
            textTransform: 'uppercase', cursor: 'pointer'
          }}>
            <span style={{ fontSize: 18 }}>+</span> Add Product
          </button>
        )}
      </div>

      <div style={{ padding: '28px 32px', display: 'flex', flexDirection: 'column', gap: 24 }}>

        {/* Alerts */}
        {success && (
          <div style={{ padding: '12px 20px', borderRadius: 16, background: 'rgba(34,197,94,0.08)', border: '1px solid rgba(34,197,94,0.2)', color: '#22c55e', fontSize: 13, fontWeight: 700 }}>
            ✓ {success}
          </div>
        )}
        {error && (
          <div style={{ padding: '12px 20px', borderRadius: 16, background: 'rgba(248,113,113,0.08)', border: '1px solid rgba(248,113,113,0.2)', color: '#f87171', fontSize: 13, fontWeight: 700 }}>
            ⚠ {error}
          </div>
        )}

        {/* ── Stat Cards ── */}
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(160px, 1fr))', gap: 16 }}>
          {[
            { label: 'Total Products',  val: products.length, icon: '📦', color: '#E8B84B' },
            { label: 'Centres Assigned', val: centreCount,    icon: '🛕', color: '#a78bfa' },
            { label: 'Low Stock',        val: lowStock,        icon: '⚠️', color: '#f59e0b' },
            { label: 'Out of Stock',     val: outOfStock,      icon: '❌', color: '#f87171' },
          ].map(s => (
            <div key={s.label} style={{
              borderRadius: 18, padding: '18px 20px',
              background: 'rgba(255,255,255,0.025)', border: `1px solid ${s.color}20`
            }}>
              <div style={{ fontSize: 22, marginBottom: 8 }}>{s.icon}</div>
              <div style={{ fontSize: 26, fontWeight: 900, color: '#FDF6E3', lineHeight: 1 }}>{s.val}</div>
              <div style={{ fontSize: 9, fontWeight: 900, textTransform: 'uppercase', letterSpacing: '0.18em', color: `${s.color}80`, marginTop: 4 }}>{s.label}</div>
            </div>
          ))}
        </div>

        {/* Inventory value */}
        <div style={{
          borderRadius: 18, padding: '16px 24px', display: 'flex', alignItems: 'center', gap: 16,
          background: 'rgba(212,175,55,0.04)', border: '1px solid rgba(212,175,55,0.14)'
        }}>
          <span style={{ fontSize: 24 }}>💰</span>
          <div>
            <p style={{ fontSize: 9, fontWeight: 900, textTransform: 'uppercase', letterSpacing: '0.2em', color: 'rgba(212,175,55,0.45)', marginBottom: 2 }}>Total Inventory Value</p>
            <p style={{ fontFamily: "'Playfair Display', serif", fontSize: 26, fontWeight: 900, color: '#E8B84B' }}>₹{fmt(totalValue)}</p>
          </div>
          <p style={{ marginLeft: 'auto', fontSize: 11, color: 'rgba(255,255,255,0.2)' }}>Across {centreCount} centre{centreCount !== 1 ? 's' : ''}</p>
        </div>

        {/* ── Filter Bar ── */}
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 10 }}>
          <div style={{ position: 'relative', flex: 1, minWidth: 200 }}>
            <span style={{ position: 'absolute', left: 12, top: '50%', transform: 'translateY(-50%)', color: 'rgba(212,175,55,0.4)', fontSize: 16 }}>🔍</span>
            <input type="text" value={search} onChange={e => setSearch(e.target.value)}
              placeholder="Search products..."
              style={{
                width: '100%', padding: '10px 14px 10px 36px', borderRadius: 12, fontSize: 13,
                background: 'rgba(255,255,255,0.05)', border: '1px solid rgba(212,175,55,0.18)',
                color: '#FDF6E3', outline: 'none', boxSizing: 'border-box'
              }} />
          </div>
          {centres.length > 0 && (
            <select value={filterCentre} onChange={e => setFilterCentre(e.target.value)}
              style={{ padding: '10px 14px', borderRadius: 12, fontSize: 13, background: 'rgba(255,255,255,0.05)', border: '1px solid rgba(212,175,55,0.18)', color: '#FDF6E3', outline: 'none' }}>
              <option value="" style={{ background: '#1a0808' }}>All Centres</option>
              {centres.map(t => <option key={t._id} value={t._id} style={{ background: '#1a0808' }}>{t.name}</option>)}
            </select>
          )}
          <select value={filterCat} onChange={e => setFilterCat(e.target.value)}
            style={{ padding: '10px 14px', borderRadius: 12, fontSize: 13, background: 'rgba(255,255,255,0.05)', border: '1px solid rgba(212,175,55,0.18)', color: '#FDF6E3', outline: 'none' }}>
            <option value="" style={{ background: '#1a0808' }}>All Categories</option>
            {CATEGORIES.map(c => <option key={c} value={c} style={{ background: '#1a0808' }}>{c}</option>)}
          </select>
        </div>

        {/* ── Products Table ── */}
        {filtered.length === 0 ? (
          <EmptyState onAdd={() => isAdmin && setModal({ mode: 'add' })} />
        ) : (
          <div style={{ borderRadius: 24, overflow: 'hidden', background: 'rgba(255,255,255,0.02)', border: '1px solid rgba(212,175,55,0.1)' }}>

            {/* Table header info */}
            <div style={{
              padding: '14px 24px', display: 'flex', alignItems: 'center', justifyContent: 'space-between',
              borderBottom: '1px solid rgba(212,175,55,0.07)', background: 'rgba(212,175,55,0.02)'
            }}>
              <span style={{ fontSize: 9, fontWeight: 900, textTransform: 'uppercase', letterSpacing: '0.2em', color: 'rgba(212,175,55,0.4)' }}>
                {filtered.length} Products {filtered.length !== products.length && `(filtered from ${products.length})`}
              </span>
              <span style={{
                fontSize: 10, fontWeight: 700, padding: '4px 10px', borderRadius: 8,
                background: 'rgba(212,175,55,0.08)', color: '#E8B84B', border: '1px solid rgba(212,175,55,0.18)'
              }}>
                🛕 Centre Shop · Admin Only
              </span>
            </div>

            <div style={{ overflowX: 'auto' }}>
              <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                <thead>
                  <tr style={{ borderBottom: '1px solid rgba(212,175,55,0.07)' }}>
                    {['Product', 'Assigned Centres', 'Category', 'Price', 'Stock', 'Actions'].map(h => (
                      <th key={h} style={{
                        padding: '14px 20px', textAlign: 'left', fontSize: 9, fontWeight: 900,
                        textTransform: 'uppercase', letterSpacing: '0.18em', color: 'rgba(212,175,55,0.38)'
                      }}>{h}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {filtered.map((p, i) => {
                    const stockStyle = getStockStyle(p.stock);
                    const assignedCentres = p.assignedCentres || [];
                    return (
                      <tr key={p._id} style={{
                        borderBottom: '1px solid rgba(255,255,255,0.025)',
                        background: i % 2 === 0 ? 'transparent' : 'rgba(255,255,255,0.007)'
                      }}>

                        {/* Product */}
                        <td style={{ padding: '14px 20px' }}>
                          <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                            {p.image ? (
                              <img src={p.image} alt={p.name} style={{ width: 40, height: 40, borderRadius: 10, objectFit: 'cover', flexShrink: 0 }}
                                onError={e => { e.target.style.display = 'none'; }} />
                            ) : (
                              <div style={{ width: 40, height: 40, borderRadius: 10, background: 'rgba(212,175,55,0.08)', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0, fontSize: 18 }}>
                                🪔
                              </div>
                            )}
                            <div>
                              <p style={{ fontSize: 13, fontWeight: 700, color: 'rgba(255,255,255,0.85)', margin: 0 }}>{p.name}</p>
                              <p style={{ fontSize: 9, color: 'rgba(255,255,255,0.2)', margin: 0, fontFamily: 'monospace' }}>
                                #{p._id?.slice(-8).toUpperCase()}
                              </p>
                            </div>
                          </div>
                        </td>

                        {/* Assigned Centres */}
                        <td style={{ padding: '14px 20px' }}>
                          {assignedCentres.length === 0 ? (
                            <span style={{ fontSize: 10, color: 'rgba(255,255,255,0.2)' }}>Unassigned</span>
                          ) : (
                            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 4 }}>
                              {assignedCentres.slice(0, 2).map(t => (
                                <span key={t._id || t} style={{
                                  fontSize: 10, padding: '3px 8px', borderRadius: 8, fontWeight: 700,
                                  background: 'rgba(167,139,250,0.1)', color: '#a78bfa', border: '1px solid rgba(167,139,250,0.2)'
                                }}>
                                  🛕 {t.name || 'Centre'}
                                </span>
                              ))}
                              {assignedCentres.length > 2 && (
                                <span style={{
                                  fontSize: 10, padding: '3px 8px', borderRadius: 8, fontWeight: 700,
                                  background: 'rgba(255,255,255,0.05)', color: 'rgba(255,255,255,0.3)'
                                }}>
                                  +{assignedCentres.length - 2} more
                                </span>
                              )}
                            </div>
                          )}
                        </td>

                        {/* Category */}
                        <td style={{ padding: '14px 20px' }}>
                          <span style={{ fontSize: 10, padding: '3px 8px', borderRadius: 8, fontWeight: 700, background: 'rgba(212,175,55,0.07)', color: 'rgba(212,175,55,0.65)' }}>
                            {p.category}
                          </span>
                        </td>

                        {/* Price */}
                        <td style={{ padding: '14px 20px' }}>
                          <span style={{ fontFamily: "'Playfair Display', serif", fontWeight: 900, fontSize: 14, color: '#E8B84B' }}>
                            ₹{fmt(p.price)}
                          </span>
                        </td>

                        {/* Stock */}
                        <td style={{ padding: '14px 20px' }}>
                          <span style={{ fontSize: 10, padding: '3px 10px', borderRadius: 8, fontWeight: 900, background: stockStyle.bg, color: stockStyle.color }}>
                            {stockStyle.label}
                          </span>
                        </td>

                        {/* Actions */}
                        <td style={{ padding: '14px 20px' }}>
                          <div style={{ display: 'flex', gap: 8 }}>
                            <button onClick={() => setModal({ mode: 'edit', data: p })}
                              style={{ width: 32, height: 32, borderRadius: 10, border: '1px solid rgba(212,175,55,0.2)', background: 'rgba(212,175,55,0.08)', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 14 }}
                              title="Edit">✏️</button>
                            <button onClick={() => handleDelete(p._id)} disabled={deleting === p._id}
                              style={{ width: 32, height: 32, borderRadius: 10, border: '1px solid rgba(248,113,113,0.2)', background: 'rgba(248,113,113,0.08)', cursor: deleting === p._id ? 'not-allowed' : 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 14, opacity: deleting === p._id ? 0.4 : 1 }}
                              title="Delete">🗑</button>
                          </div>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>

            {/* Footer */}
            <div style={{
              padding: '12px 24px', display: 'flex', justifyContent: 'space-between', alignItems: 'center',
              borderTop: '1px solid rgba(212,175,55,0.07)', background: 'rgba(212,175,55,0.015)'
            }}>
              <span style={{ fontSize: 10, color: 'rgba(212,175,55,0.35)', fontWeight: 700 }}>
                {filtered.length} of {products.length} products
              </span>
              <span style={{ fontSize: 10, color: 'rgba(255,255,255,0.15)' }}>
                🛕 Live Stream Exclusive · Admin Managed
              </span>
            </div>
          </div>
        )}
      </div>

      {/* ── Modal ── */}
      {modal && (
        <ProductModal
          mode={modal.mode}
          initial={modal.data}
          centres={centres}
          onSave={handleSave}
          onClose={() => setModal(null)}
          saving={saving}
        />
      )}
    </div>
  );
};

export default CentreShop;
